import { z } from "zod";

/**
 * 不審なIPの制限（#110、docs/42）。共通の土台＝どの製品（現場力・営業力・店舗力）でも同じ。
 *   海外の IP からの 会員登録・ログイン・属性変更 は、メールの確認コード（6桁）を求める。
 *   止める IP（Spamhaus DROP・Tor の出口・手で止めた IP・失敗が続いた IP）からは断る。
 * 画面の文はここに1か所（docs/10 §5.1.2：簡潔に・業務の言葉で）
 */
export const CODE_LIMITS = {
  /** 桁数 */
  digits: 6,
  /** 期限（分） */
  ttlMin: 10,
  /** まちがえてよい回数 */
  maxTries: 5,
  /** 送り直しの回数 */
  maxResends: 3,
  /** 属性変更の前に確かめたあと、聞き直さない時間（分） */
  stepUpMin: 30,
  /** 海外の1つの IP からの会員登録（1日） */
  foreignSignupsPerDay: 3,
} as const;

/** 確認コードを付けて同じ操作を送り直すときのヘッダー */
export const CODE_HEADERS = { challenge: "x-gb-challenge", code: "x-gb-code" } as const;

export const IP_GUARD_MSG = {
  blocked: "このネットワークからはご利用いただけません。別の回線（スマートフォンの回線など）でお試しください",
  signupLimit: "このネットワークからの会員登録は1日3件までです。時間をおいてお試しください",
  codeTitle: "確認コード入力",
  codeSent: (to: string) => `${to} 宛てに確認コードを送りました。`,
  codeLabel: "確認コード（6桁）",
  codeSubmit: "確認",
  codeResend: "確認コード再送",
  codeResent: "確認コードを再送しました",
  codeCancel: "取消",
  codeFormat: "6桁の数字を入力してください",
  codeWrong: (left: number) => `確認コードが違います（残り${left}回）`,
  codeLocked: "確認コードの入力に5回失敗したため、このコードは無効です。最初から操作してください",
  codeExpired: "確認コードの有効期限（10分）が切れました。確認コードを再送してください",
  codeInvalid: "この確認コードは無効です。最初から操作してください",
  codeResendLimit: "確認コードの再送は3回までです。最初から操作してください",
  codeTooMany: "確認コードの送信回数が上限に達しました。1時間ほどおいてお試しください",
  codeCancelled: "確認コードの入力を中止しました",
  /** 欄の中の右端のボタン（#111）。コピーしてきた文からコードを拾って入れ、そのまま確認する */
  codePaste: "貼り付け",
  codePasteNone: "コピーした文字に6桁の確認コードがありません",
  codePasteDenied: "貼り付けできませんでした。欄を長押し（右クリック）して貼り付けてください",
  /** DB-IP（CC BY 4.0）の出典。国の判定を使う画面（ログイン・会員登録）の下とプライバシーポリシー */
  attribution: "IP Geolocation by DB-IP",
  attributionUrl: "https://db-ip.com",
} as const;

export const CodePurpose = z.enum(["login", "signup", "step_up"]);
export type CodePurpose = z.infer<typeof CodePurpose>;

/** 403 AUTH_CODE_REQUIRED の中身（RFC 9457 の拡張） */
export const CodeChallenge = z.object({
  challengeToken: z.string().regex(/^[0-9a-f]{64}$/),
  sentTo: z.string(),
  purpose: CodePurpose,
  expiresAt: z.string(),
});
export type CodeChallenge = z.infer<typeof CodeChallenge>;

export const CodeResendRequest = z.object({ challengeToken: z.string().regex(/^[0-9a-f]{64}$/, IP_GUARD_MSG.codeInvalid) });
export type CodeResendRequest = z.infer<typeof CodeResendRequest>;
export const CodeResendResponse = z.object({ sentTo: z.string(), expiresAt: z.string() });
export type CodeResendResponse = z.infer<typeof CodeResendResponse>;

const toHalfDigits = (raw: string) => raw.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));

/** 打たれたコードを半角の6桁に（全角数字・空白・ハイフンを許す）。6桁にならなければ null */
export function normalizeCode(raw: string): string | null {
  const half = toHalfDigits(raw).replace(/[\s\-－ー]/g, "");
  return /^[0-9]{6}$/.test(half) ? half : null;
}

/**
 * 貼り付けた文から確認コードを拾う（#111）。メールの「確認コード：482913」の行ごと・本文ごとコピーしても6桁だけ。
 * 「確認コード」のすぐ後ろの6桁 → 文の中にちょうど1つだけある6桁（前後に数字が続かないもの）の順。決まらなければ null
 */
export function codeFromText(raw: string): string | null {
  const direct = normalizeCode(raw);
  if (direct) return direct;
  const half = toHalfDigits(raw);
  const labeled = /確認コード\s*[：:]?\s*([0-9]{6})(?![0-9])/.exec(half);
  if (labeled) return labeled[1]!;
  const runs = half.match(/(?<![0-9])[0-9]{6}(?![0-9])/g) ?? [];
  return runs.length === 1 ? runs[0]! : null;
}
